import { toast } from '@/components/toast';
import { didApiUrl, mixpanelKey, webSocketUrl } from '@/environment';
import { loadAgentsClientScript } from '@/pages/agents/loadAgentsClientScript';
import { isAgentV2 } from '@/pages/avatar/hooks/useAgentIsV2';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useClientKey } from '@/state/agent';
import { AppRoutes } from '@/utils/types';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
    agentId: string;
    from: string;
    className?: string;
}

export const AgentPreview = ({ agentId, from, className }: Props) => {
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const { isAnonymous, loginWithRedirect, user } = useAuth();
    const { data: clientKey } = useClientKey({ isAnonymous });

    const targetRef = useRef<HTMLDivElement>(null);

    const isV2 = !!isAgentV2(agentId);

    useEffect(() => {
        mixpanel?.track(from, {
            event: 'view',
            agentId,
        });

        if (clientKey) {
            loadAgentsClientScript({ isV2 }).then(({ init }) => {
                init({
                    agentId,
                    clientKey: clientKey.client_key,
                    mode: 'full',
                    targetElement: targetRef.current!,
                    monitor: true,
                    onError(err: Error) {
                        console.log(err);

                        try {
                            const jsonError = JSON.parse(err.message);
                            if (jsonError.kind === 'RequestTimeoutError') {
                                toast.error({
                                    toastId: 'timeout',
                                    title: 'Request timeout, please try again later.',
                                });
                            } else if (jsonError.description === 'Agent not found') {
                                toast.error({
                                    toastId: 'agent-not-found',
                                    title: 'The agent you are trying to access does not exist.',
                                });

                                navigate(AppRoutes.Agent);
                            }
                        } catch {}
                    },
                    offline: {
                        onSend: () => {
                            mixpanel?.track('go-to-signup', { event: 'click', from, isAnonymous });
                            loginWithRedirect({ redirectUri: window.location.href });
                        },
                    },
                    didSocketApiUrl: webSocketUrl,
                    didApiUrl,
                    customMixpanelKey: mixpanelKey,
                    externalId: user?.sub ?? '',
                    mixpanelAdditionalProperties: {
                        plan: user?.planGroup,
                    },
                });
            });
        } else {
            toast.error({
                toastId: 'invalid key',
                title: 'Missing agent key',
            });
        }
    }, [agentId]);

    return <div ref={targetRef} className={className} />;
};
